/**
 * WelcomeModal (formerly IndustryPickerModal) — 3-step onboarding welcome
 *
 * Step 1: Industry pick (6 cards)
 * Step 2: Source language (LanguagePicker với search + flags)
 * Step 3: Target language
 *
 * Outputs: industry, sourceLang, targetLang → saved vào Supabase + localStorage
 * khi user click "Bắt đầu tour" ở step cuối.
 */

import React, { useState } from 'react';
import {
  X, Gift, ArrowRight, ArrowLeft, Check,
  Scale, Briefcase, BarChart3, Package, Building2, FileEdit,
  Globe, Languages as LanguagesIcon, Shield, Cloud, AlertTriangle,
} from 'lucide-react';
import { INDUSTRIES, type IndustryId } from './tour-steps';
import LanguagePicker from './LanguagePicker';
import { DEFAULT_SOURCE_LANG, DEFAULT_TARGET_LANG } from '../../../utils/languages';
import { usePrivacyStore, type PrivacyMode } from '../../../store/usePrivacyStore';

const ICON_MAP = {
  scale: Scale,
  briefcase: Briefcase,
  chart: BarChart3,
  package: Package,
  building: Building2,
  edit: FileEdit,
} as const;

export interface OnboardingChoices {
  privacyMode: PrivacyMode;
  industry: IndustryId;
  sourceLang: string;
  targetLang: string;
}

interface Props {
  isOpen: boolean;
  userName?: string;
  onStart: (choices: OnboardingChoices) => void;
  onSkip: () => void;
}

type StepNum = 1 | 2 | 3 | 4;
const TOTAL_STEPS = 4;

const WelcomeModal: React.FC<Props> = ({ isOpen, userName, onStart, onSkip }) => {
  const currentMode = usePrivacyStore(s => s.mode);
  const [step, setStep] = useState<StepNum>(1);
  const [privacyMode, setPrivacyMode] = useState<PrivacyMode>(currentMode);
  const [industry, setIndustry] = useState<IndustryId | null>(null);
  const [sourceLang, setSourceLang] = useState<string>(DEFAULT_SOURCE_LANG);
  const [targetLang, setTargetLang] = useState<string>(DEFAULT_TARGET_LANG);

  if (!isOpen) return null;

  const firstName = userName?.split(/[.\s@]/)[0] || 'bạn';
  const displayName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  const canGoNext =
    (step === 1 && !!privacyMode) ||
    (step === 2 && industry !== null) ||
    (step === 3 && sourceLang !== '') ||
    (step === 4 && targetLang !== '' && targetLang !== sourceLang);

  const handleNext = () => {
    if (step === TOTAL_STEPS && industry) {
      onStart({ privacyMode, industry, sourceLang, targetLang });
      return;
    }
    setStep(s => (s + 1) as StepNum);
  };

  const handleBack = () => {
    if (step > 1) setStep(s => (s - 1) as StepNum);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header + progress */}
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 flex items-center justify-between border-b border-indigo-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md animate-pulse-soft">
              {step === 1 && <Shield className="w-5 h-5 text-white" />}
              {step === 2 && <Gift className="w-5 h-5 text-white" />}
              {step === 3 && <Globe className="w-5 h-5 text-white" />}
              {step === 4 && <LanguagesIcon className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="font-black text-slate-800 leading-none">
                {step === 1 && `Chào ${displayName} — chế độ riêng tư`}
                {step === 2 && 'Bạn làm ngành gì?'}
                {step === 3 && 'Ngôn ngữ nguồn'}
                {step === 4 && 'Ngôn ngữ đích'}
              </h3>
              <p className="text-[11px] text-slate-500 mt-1">
                Bước {step}/{TOTAL_STEPS} — setup 4 thông tin cơ bản
              </p>
            </div>
          </div>
          <button
            onClick={onSkip}
            className="p-2 rounded-lg hover:bg-white/60 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Đóng"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 pt-5 pb-1">
          {[1, 2, 3, 4].map(n => (
            <div
              key={n}
              className={`
                h-1.5 rounded-full transition-all duration-300
                ${n === step ? 'w-8 bg-indigo-600' : n < step ? 'w-6 bg-indigo-300' : 'w-6 bg-slate-200'}
              `}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-6 pt-4 pb-6 min-h-[380px] flex flex-col">
          {step === 1 && (
            <PrivacyStep
              selected={privacyMode}
              onSelect={setPrivacyMode}
            />
          )}
          {step === 2 && (
            <IndustryStep
              selected={industry}
              onSelect={setIndustry}
            />
          )}
          {step === 3 && (
            <LanguageStep
              title="Bạn viết tài liệu bằng ngôn ngữ gì?"
              subtitle="Đây là ngôn ngữ của bản gốc — văn bản bạn muốn dịch ĐI."
              value={sourceLang}
              onChange={setSourceLang}
              label="Ngôn ngữ nguồn"
              excludeCode={undefined}
            />
          )}
          {step === 4 && (
            <LanguageStep
              title="Dịch sang ngôn ngữ nào?"
              subtitle="Bản dịch output sẽ bằng ngôn ngữ này."
              value={targetLang}
              onChange={setTargetLang}
              label="Ngôn ngữ đích"
              excludeCode={sourceLang}
            />
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between gap-3 mt-auto pt-5 border-t border-slate-100">
            {step > 1 ? (
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-2 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Trước
              </button>
            ) : (
              <button
                onClick={onSkip}
                className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-2 transition-colors"
              >
                Bỏ qua — tôi tự tìm hiểu
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className="
                inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold
                shadow-lg shadow-indigo-100
                hover:shadow-xl hover:shadow-indigo-200 hover:-translate-y-0.5
                active:scale-95
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0
                transition-all
              "
            >
              {step === TOTAL_STEPS ? (
                <>
                  <Check className="w-4 h-4" />
                  Bắt đầu tour
                </>
              ) : (
                <>
                  Tiếp
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────

interface PrivacyStepProps {
  selected: PrivacyMode;
  onSelect: (mode: PrivacyMode) => void;
}

const PrivacyStep: React.FC<PrivacyStepProps> = ({ selected, onSelect }) => (
  <>
    <div className="mb-4">
      <p className="text-sm text-slate-700 leading-relaxed">
        Trước khi dùng app, chọn <strong className="text-indigo-700">chế độ xử lý tài liệu</strong> phù
        hợp — liên quan trực tiếp đến bảo mật file hợp đồng của bạn.
      </p>
    </div>

    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
      Chế độ riêng tư
    </p>

    <div className="space-y-2.5">
      {/* Cloud sync / Dịch Ngầm */}
      <button
        onClick={() => onSelect('cloud_sync')}
        className={`
          w-full p-4 rounded-xl border-2 text-left transition-all
          ${selected === 'cloud_sync'
            ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-100'
            : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/50'
          }
        `}
      >
        <div className="flex items-start gap-3">
          <div className={`
            w-10 h-10 rounded-xl flex items-center justify-center shrink-0
            ${selected === 'cloud_sync' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}
          `}>
            <Cloud className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-slate-800">Dịch Ngầm</span>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                KHUYÊN DÙNG
              </span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              File lưu trên máy bạn. Server backup tạm <b>đã mã hoá</b> để
              <b> khôi phục khi mất điện / đóng tab giữa chừng</b>. 99% user chọn.
            </p>
          </div>
        </div>
      </button>

      {/* Incognito / Dịch Ẩn Danh */}
      <button
        onClick={() => onSelect('incognito')}
        className={`
          w-full p-4 rounded-xl border-2 text-left transition-all
          ${selected === 'incognito'
            ? 'border-fuchsia-500 bg-fuchsia-50 shadow-md shadow-fuchsia-100'
            : 'border-slate-200 hover:border-fuchsia-200 hover:bg-fuchsia-50/50'
          }
        `}
      >
        <div className="flex items-start gap-3">
          <div className={`
            w-10 h-10 rounded-xl flex items-center justify-center shrink-0
            ${selected === 'incognito' ? 'bg-fuchsia-600 text-white' : 'bg-slate-100 text-slate-500'}
          `}>
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-slate-800">Dịch Ẩn Danh</span>
              <span className="text-[10px] font-bold text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded">
                TỐI MẬT
              </span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              <b>Zero-server touch</b> — không backup, không log nội dung.
              Hợp đồng M&A, NDA? Chọn mode này.
            </p>
          </div>
        </div>
      </button>

      {/* Warning cho Ẩn Danh */}
      {selected === 'incognito' && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <b>Đánh đổi</b>: mất điện / đóng tab giữa chừng = <b>dịch lại từ đầu</b>.
            Vì không backup server, app không thể recover progress.
          </p>
        </div>
      )}
    </div>

    <p className="text-[11px] text-slate-500 mt-4 text-center">
      Đổi được sau trong Sidebar (góc dưới trái — nút <b>"Dịch Ngầm" / "Dịch Ẩn Danh"</b>)
    </p>
  </>
);

interface IndustryStepProps {
  selected: IndustryId | null;
  onSelect: (id: IndustryId) => void;
}

const IndustryStep: React.FC<IndustryStepProps> = ({ selected, onSelect }) => (
  <>
    <div className="mb-4">
      <p className="text-sm text-slate-700 leading-relaxed">
        Dịch hợp đồng tiếng Anh mất cả buổi sáng? Copy-paste từng đoạn, xong chỉnh format nửa tiếng?
      </p>
      <p className="text-sm text-slate-700 leading-relaxed mt-2">
        Bọn mình xây app này để bạn <strong className="text-indigo-700">deliver trước khi sếp họp</strong>.
        Chọn ngành → AI dịch hợp <em>ngữ cảnh công việc</em> của bạn.
      </p>
    </div>

    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
      Ngành bạn đang làm
    </p>
    <div className="grid grid-cols-3 gap-2.5">
      {INDUSTRIES.map(ind => {
        const Icon = ICON_MAP[ind.icon as keyof typeof ICON_MAP];
        const isSelected = selected === ind.id;
        return (
          <button
            key={ind.id}
            onClick={() => onSelect(ind.id)}
            className={`
              p-3 rounded-xl border-2 text-center transition-all
              ${isSelected
                ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-100 scale-[1.02]'
                : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/50'
              }
            `}
          >
            <Icon className={`w-5 h-5 mx-auto mb-1.5 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} />
            <div className={`text-xs font-bold mb-0.5 ${isSelected ? 'text-indigo-900' : 'text-slate-700'}`}>
              {ind.name}
            </div>
            <div className="text-[10px] text-slate-500 leading-tight">{ind.hint}</div>
          </button>
        );
      })}
    </div>
  </>
);

interface LanguageStepProps {
  title: string;
  subtitle: string;
  value: string;
  onChange: (code: string) => void;
  label: string;
  excludeCode?: string;
}

const LanguageStep: React.FC<LanguageStepProps> = ({ title, subtitle, value, onChange, label, excludeCode }) => (
  <>
    <div className="mb-6">
      <h4 className="text-base font-bold text-slate-800 mb-1.5">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{subtitle}</p>
    </div>

    <LanguagePicker
      value={value}
      onChange={onChange}
      label={label}
      excludeCode={excludeCode}
    />

    {excludeCode && value === excludeCode && (
      <p className="mt-3 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
        Ngôn ngữ nguồn và đích không được trùng nhau
      </p>
    )}

    <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-100">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
        Sau này muốn đổi?
      </p>
      <p className="text-xs text-slate-600 leading-relaxed">
        Đây là default cho <b>tất cả project mới</b> bạn tạo. Mỗi project riêng có thể override
        trong tab <b>Cấu hình</b>, hoặc đổi default trong <b>Cài đặt</b>.
      </p>
    </div>
  </>
);

export default WelcomeModal;
